import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCashOrderDefaults, getCashOrdersList, getCashOrderDetails } from '../../../../actions/cashActions';
import { getPlasticTypesList } from '../../../../actions/maintenanceActions';
import Regex from '../../../../constants/regex';
import type { AmpolCashOrdersContext } from '../../../../selectors/ampolCashOrdersSelectors';

/**
 * Ampol Cash Data Hook
 *
 * Manages data lifecycle, API calls, and global search integration for Ampol Cash Orders.
 *
 * @param context - Redux context from memoized selector
 * @param filters - Filter state from useAmpolCashFilters
 * @param modals - Modal state from useAmpolCashModals
 * @returns Data state and API call handlers
 */
interface UseAmpolCashDataProps {
  context: AmpolCashOrdersContext;
  filters: {
    sortColumn: string;
    sortDirection: 'asc' | 'desc';
    cashOrdersQueryParams: any;
    pageSize: number;
    offset: number;
    defaultFilterValues: any;
    defaultSearchValues: any;
    setSearchTerm: (term: string) => void;
    updateQueryParams: (updates: any) => void;
    resetFiltersOnAccountChange: () => void;
  };
  modals: {
    selectedOrder: any;
    setSelectedOrder: (order: any) => void;
    selectedCardDetails: any;
    setSelectedCardDetails: (details: any) => void;
    setIsCardDetailsClicked: (clicked: boolean) => void;
  };
}

export const useAmpolCashData = ({ context, filters, modals }: UseAmpolCashDataProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isMounted = useRef(false);

  // Local state for card details table
  const [cardDetailsTableData, setCardDetailsTableData] = useState<any[]>([]);

  // Destructure for cleaner access
  const {
    assignedGrandfatherClientNumber,
    cashOrderCardDetails,
    cashOrderDetails,
    cashOrderCardDetailsPageNumber,
    activateCardSingleIsLoading,
    activateCardSingleSuccess,
    activateMultipleCardsLoaded,
    globalSearchLoaded,
    globalSearchResult,
    cashOrdersList,
  } = context;

  const {
    sortColumn,
    sortDirection,
    cashOrdersQueryParams,
    pageSize,
    offset,
    setSearchTerm,
    updateQueryParams,
    resetFiltersOnAccountChange,
  } = filters;

  const { selectedOrder, setSelectedOrder, selectedCardDetails, setSelectedCardDetails, setIsCardDetailsClicked } =
    modals;

  // Store unstable callbacks in refs to prevent infinite loops
  // Store unstable callbacks in refs (prevents re-render loops)
  const updateQueryParamsRef = useRef(updateQueryParams);
  const setSearchTermRef = useRef(setSearchTerm);
  const setSelectedCardDetailsRef = useRef(setSelectedCardDetails);
  const setSelectedOrderRef = useRef(setSelectedOrder);
  const setIsCardDetailsClickedRef = useRef(setIsCardDetailsClicked);
  const resetFiltersRef = useRef(resetFiltersOnAccountChange);

  // Update refs every render (doesn't trigger re-renders)
  useEffect(() => {
    updateQueryParamsRef.current = updateQueryParams;
    setSearchTermRef.current = setSearchTerm;
    setSelectedCardDetailsRef.current = setSelectedCardDetails;
    setSelectedOrderRef.current = setSelectedOrder;
    setIsCardDetailsClickedRef.current = setIsCardDetailsClicked;
    resetFiltersRef.current = resetFiltersOnAccountChange;
  });

  // API call to fetch cash orders list
  const handleCashOrderSubmitted = useCallback(() => {
    dispatch(
      getCashOrdersList(
        assignedGrandfatherClientNumber,
        assignedGrandfatherClientNumber,
        pageSize,
        offset,
        sortColumn,
        sortDirection,
        cashOrdersQueryParams.filterValues.dateFrom,
        cashOrdersQueryParams.filterValues.dateTo,
        cashOrdersQueryParams.filterValues.orderNumber,
        cashOrdersQueryParams.filterValues.orderValueFrom,
        cashOrdersQueryParams.filterValues.orderValueTo,
        cashOrdersQueryParams.filterValues.orderStatus,
        cashOrdersQueryParams.searchValues.cardNumber,
        cashOrdersQueryParams.searchValues.orderNumber
      )
    );
  }, [dispatch, assignedGrandfatherClientNumber, pageSize, offset, sortColumn, sortDirection, cashOrdersQueryParams]);

  // Effect: Refresh order details after single card activation
  useEffect(() => {
    if (activateCardSingleSuccess) {
      dispatch(
        getCashOrderDetails(
          selectedOrder?.orderNumber,
          assignedGrandfatherClientNumber,
          assignedGrandfatherClientNumber,
          cashOrderCardDetailsPageNumber,
          5
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activateCardSingleSuccess,
    selectedOrder?.orderNumber, // Narrow dep: only re-run when orderNumber changes, not any field
    assignedGrandfatherClientNumber,
    cashOrderCardDetailsPageNumber,
    dispatch,
  ]);

  // Effect: Refresh order details after multiple cards activation
  useEffect(() => {
    if (activateMultipleCardsLoaded) {
      dispatch(
        getCashOrderDetails(
          selectedOrder?.orderNumber,
          assignedGrandfatherClientNumber,
          assignedGrandfatherClientNumber,
          cashOrderCardDetailsPageNumber,
          5
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activateMultipleCardsLoaded,
    selectedOrder?.orderNumber, // Narrow dep: only re-run when orderNumber changes, not any field
    assignedGrandfatherClientNumber,
    cashOrderCardDetailsPageNumber,
    dispatch,
  ]);

  // Effect: Update selected card details after order details refresh
  useEffect(() => {
    if (!activateCardSingleIsLoading && activateCardSingleSuccess && cashOrderDetails) {
      const updatedCardDetails = cashOrderDetails.result?.find(
        (obj: any) => obj?.cardNumber === selectedCardDetails?.cardNumber
      );
      if (updatedCardDetails) {
        setSelectedCardDetailsRef.current(updatedCardDetails);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activateCardSingleIsLoading, activateCardSingleSuccess, cashOrderDetails, selectedCardDetails?.cardNumber]);

  // Effect: Update card details table data when card details change
  useEffect(() => {
    if (cashOrderCardDetails) {
      setCardDetailsTableData([
        {
          cardNumber: cashOrderCardDetails.cardNumber,
          cardValue: cashOrderCardDetails.cardValue,
          mediaType: cashOrderCardDetails.mediaType,
          status: cashOrderCardDetails.status,
        },
      ]);

      // Clear global search state if navigated from global search
      if (location.state === 'globalSearch') {
        navigate(location.pathname, { replace: true });
      }
    }
  }, [cashOrderCardDetails, location.state, location.pathname, navigate]);

  // Effect: Initialize data on account change
  useEffect(() => {
    if (assignedGrandfatherClientNumber && assignedGrandfatherClientNumber.match(Regex.CASH_ACCOUNT)) {
      dispatch(getCashOrderDefaults(assignedGrandfatherClientNumber, assignedGrandfatherClientNumber));
      dispatch(getPlasticTypesList(100, 0, '', '', 'c_')); // TODO add limit to env variable

      if (isMounted.current) {
        resetFiltersRef.current();
      }
    }
    isMounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedGrandfatherClientNumber, dispatch]);

  // Effect: Handle global search integration
  useEffect(() => {
    if (globalSearchLoaded && globalSearchResult && cashOrdersList.length > 0) {
      const currentParams = cashOrdersQueryParams;
      updateQueryParamsRef.current({
        filterValues: {
          ...currentParams.filterValues,
          pageNumber: 1,
        },
        searchValues: {
          ...currentParams.searchValues,
          cardNumber: globalSearchResult,
        },
        changedFromOnClear: false,
      });
      setSearchTermRef.current(globalSearchResult);
      if (cashOrderCardDetails) {
        setSelectedCardDetailsRef.current({ ...cashOrderCardDetails });
      }
      if (cashOrdersList[0]) {
        setSelectedOrderRef.current(cashOrdersList[0]);
      }
      setIsCardDetailsClickedRef.current(true);
    } else if (isMounted.current && !globalSearchLoaded && globalSearchResult) {
      const currentParams = cashOrdersQueryParams;
      updateQueryParamsRef.current({
        filterValues: {
          ...currentParams.filterValues,
          pageNumber: 1,
        },
        searchValues: {
          ...currentParams.searchValues,
        },
        changedFromOnClear: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSearchLoaded, globalSearchResult, cashOrdersList]);

  // Effect: Fetch orders list when filters/sort changes
  useEffect(() => {
    if (
      assignedGrandfatherClientNumber &&
      assignedGrandfatherClientNumber.match(Regex.CASH_ACCOUNT) &&
      !cashOrdersQueryParams.changedFromOnClear &&
      location.state !== 'globalSearch'
    ) {
      handleCashOrderSubmitted();

      const currentParams = cashOrdersQueryParams;
      updateQueryParamsRef.current({
        filterValues: {
          ...currentParams.filterValues,
          pageNumber: 1,
        },
        searchValues: {
          ...currentParams.searchValues,
        },
        changedFromOnClear: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortColumn,
    sortDirection,
    cashOrdersQueryParams.filterValues,
    cashOrdersQueryParams.searchValues.orderNumber,
    cashOrdersQueryParams.searchValues.cardNumber,
    cashOrdersQueryParams.changedFromOnClear,
    assignedGrandfatherClientNumber,
    location.state,
  ]);

  return {
    // State
    cardDetailsTableData,

    // API handlers
    handleCashOrderSubmitted,
  };
};
